import type { Home } from '@lmc/ts-common'
import { FormCard, FormItem, Icon, IconType } from '@lmc/ui-kit'
import { Heading, MoreInfoLink, Text } from '@lmc/ui-kit/src/general'
import { Switch } from 'antd'
import { getNominative } from 'common/utils'
import useFeatureFlags from 'hooks/useFeatureFlags'
import { NAMESPACE } from 'locale'
import { pbsConstants } from 'locale/pbs'
import type { FC } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { Link } from 'react-router-dom'
import styled from 'styled-components'

const PBS_LINK =
  'https://support.logmycare.co.uk/en/articles/9189836-how-do-positive-behavioural-support-plans-work-on-log-my-care'

type Props = {
  positiveBehaviourGranted?: boolean
  positiveBehaviourEnabled?: boolean
  onTooglePositiveBehaviour: (checked: boolean) => void
}

export const CarePlanPreferences: FC<Props> = ({
  positiveBehaviourGranted,
  positiveBehaviourEnabled,
  onTooglePositiveBehaviour
}) => {
  const { t } = useTranslation(NAMESPACE.PBS)

  const preferredCarePlansNominative = useSelector(
    (state: { global: { home: Home } }) =>
      state.global.home.preferredCarePlansNominative
  )

  const preferredCarePlanNameNominative = getNominative(
    preferredCarePlansNominative,
    'Care Plan'
  )

  const preferredCarePlanName = getNominative(
    preferredCarePlansNominative,
    'Care Plans'
  )

  const pbsEnabled = useFeatureFlags({ flagNames: ['pbs'] })?.data.pbs.enabled

  if (!pbsEnabled || !positiveBehaviourGranted) {
    return null
  }

  return (
    <StyledFormCard
      title={t(pbsConstants.TITLE_PREFERENCES, {
        variable: preferredCarePlanNameNominative
      })}
    >
      <StyledHeading level={3}>
        {t(pbsConstants.BE_AWARE, {
          variable: preferredCarePlanName
        })}{' '}
        <MoreInfoLink inline helpArticleUrl={PBS_LINK} />
      </StyledHeading>
      <ItemWrapper isDisabled={!positiveBehaviourEnabled}>
        <PBSIconWrapper>
          <Icon customIcon={IconType.CarePlanPBSAdlIcon} />
        </PBSIconWrapper>
        <StyledText>{t(pbsConstants.TITLE_BEHAVIOUR)}</StyledText>
        <StyledFormItem>
          <Switch
            onChange={onTooglePositiveBehaviour}
            checked={positiveBehaviourEnabled}
          />
        </StyledFormItem>
      </ItemWrapper>
    </StyledFormCard>
  )
}

const StyledFormCard = styled(FormCard)`
  margin-bottom: 50px;
`

const StyledFormItem = styled(FormItem)`
  margin-left: auto;
  margin-right: 0px;
  max-width: unset;
`

const ItemWrapper = styled.div<{ isDisabled?: boolean }>`
  padding: 16px;
  border: 0.5px solid;
  border-color: ${(props) => props.theme.color.neutral.color8};
  border-radius: 8px;
  background-color: white;
  width: 100%;
  max-width: unset;
  display: flex;
  align-items: center;
  opacity: ${(props) => (props?.isDisabled ? 0.5 : 1)};
`

const StyledHeading = styled(Heading)`
  color: ${(props) => props.theme.color.neutral.color4};
  border-bottom: 0.5px solid;
  border-color: ${(props) => props.theme.color.neutral.color8};
  padding-bottom: 10px;
  font-size: 20px;
`

const StyledText = styled(Text)`
  color: ${(props) => props.theme.color.neutral.color5};
  margin-left: 10px;
  width: 90%;
`

const PBSIconWrapper = styled.div`
  background-color: #9966fb;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 5px;
`
